import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { API_ENDPOINTS } from '../../config/api';
import StashitLogo from '../../components/Logo/StashitLogo';

const EmailConfirmPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const handleResendConfirmation = async () => {
    if (!userEmail) {
      toast.error('Email address not available for resending');
      return;
    }

    setResendLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.RESEND_CONFIRMATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: userEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Confirmation email sent! Check your inbox.');
      } else {
        toast.error(data.message || 'Failed to resend confirmation email');
      }
    } catch (error) {
      console.error('Resend confirmation error:', error);
      toast.error('Failed to resend confirmation email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Get the URL hash parameters
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const error = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');
        const email = hashParams.get('email');

        // Store email for potential resend
        if (email) {
          setUserEmail(email);
        }

        // Check for errors in the URL
        if (error) {
          console.error('Email confirmation error:', error, errorDescription);

          if (error === 'otp_expired') {
            setError('Email confirmation link has expired. Please request a new confirmation email.');
            toast.error('Confirmation link expired. Please try registering again.');
          } else {
            setError(`Email confirmation failed: ${errorDescription || error}`);
            toast.error('Email confirmation failed. Please try again.');
          }

          setLoading(false);
          return;
        }

        // If we have tokens, set the session
        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            throw sessionError;
          }

          // Success! Redirect to login page
          toast.success('Email confirmed successfully! Please sign in.');
          navigate('/auth/login');
          return;
        }

        // If no tokens, check if user is already confirmed
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError) {
          throw userError;
        }

        if (user && user.email_confirmed_at) {
          toast.success('Email already confirmed! Please sign in.');
          navigate('/auth/login');
          return;
        }

        // If we get here, something went wrong
        setError('Unable to confirm email. Please try registering again.');
        toast.error('Email confirmation failed. Please try again.');

      } catch (error: any) {
        console.error('Email confirmation error:', error);
        setError(error.message || 'An error occurred during email confirmation');
        toast.error('Email confirmation failed. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    handleEmailConfirmation();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700 p-4">
        <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md text-center">
          <div className="flex justify-center mb-6">
            <StashitLogo width={150} height={45} showTagline={false} />
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Confirming Email...</h2>
          <p className="text-gray-600">Please wait while we confirm your email address.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700 p-4">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md text-center">
        <div className="flex justify-center mb-6">
          <StashitLogo width={150} height={45} showTagline={false} />
        </div>

        {error ? (
          <>
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Email Confirmation Failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              {userEmail && (
                <button
                  onClick={handleResendConfirmation}
                  disabled={resendLoading}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendLoading ? 'Sending...' : 'Resend Confirmation Email'}
                </button>
              )}
              <button
                onClick={() => navigate('/auth/signup')}
                className="btn-secondary w-full"
              >
                Try Registering Again
              </button>
              <button
                onClick={() => navigate('/auth/login')}
                className="btn-secondary w-full"
              >
                Back to Login
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-green-500 text-6xl mb-4">✅</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Email Confirmed!</h2>
            <p className="text-gray-600 mb-6">Your email has been successfully confirmed. You can now sign in to your account.</p>
            <button
              onClick={() => navigate('/auth/login')}
              className="btn-primary w-full"
            >
              Sign In
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailConfirmPage;
